import React, { useCallback } from 'react';
import { Text, Box, Paragraph, Wrapper } from 'roo-ui/components';
import Modal from 'components/Modal';
import TextButton from 'components/TextButton';
import { useModal } from 'lib/hooks';
import { useDataLayer } from 'hooks/useDataLayer';

const VoucherTerms = ({ ...rest }) => {
  const { openModal, modalProps } = useModal();
  const { emitInteractionEvent } = useDataLayer();

  const handleOnClick = useCallback(() => {
    openModal();
    emitInteractionEvent({ type: 'Voucher Link', value: 'Terms and Conditions Selected' });
  }, [openModal, emitInteractionEvent]);

  return (
    <Wrapper {...rest}>
      <TextButton color="greys.steel" hoverColor="greys.steel" textDecoration="underline" onClick={handleOnClick} py={1} fontSize="sm">
        Voucher Terms and Conditions
      </TextButton>
      {modalProps.isOpen && (
        <Modal {...modalProps} padding="0" title="Voucher Terms and Conditions">
          <Text display="block" p={6} data-testid="voucher-terms">
            <Box>
              <Paragraph display="block">
                Qantas Hotels and Qantas Holidays vouchers include promotional and goodwill vouchers (Vouchers); credit note vouchers
                (issued in lieu of a booking which has been cancelled where booking terms permit) (Credit Vouchers) and credit vouchers
                linked to a specific property (Property Specific Vouchers).
              </Paragraph>
              <Paragraph display="block">
                Voucher terms and conditions may vary. You should always read the terms and conditions specific to your voucher in
                combination with these terms and conditions. All of our Vouchers may be transferred, but are not valid if sold and are not
                exchangeable or redeemable for cash or other goods or services.
              </Paragraph>

              <Text fontWeight="bold" display="block">
                Promotional and goodwill vouchers (Vouchers)
              </Text>
              <Paragraph display="block">
                Promotional and goodwill vouchers are complimentary vouchers issued by Qantas for promotional purposes, as a reward or
                benefit under the Qantas Frequent Flyer program, or for other goodwill purposes. These Vouchers can only be used once, and
                any unused portion of the Voucher will be forfeited.
              </Paragraph>
              <Paragraph display="block">
                Please check any additional terms and conditions specific to your Voucher to determine any additional conditions such as
                minimum spend requirements or an expiry date that may apply to your Voucher.
              </Paragraph>
              <Paragraph display="block">
                Vouchers can be used for part or full payment of your booking with Qantas Hotels or Qantas Holidays. If after using your
                Voucher there is a balance remaining to be paid, you must pay this balance with a credit/charge card or through the use of
                Qantas Points if you are a member of the Qantas Frequent Flyer program.
              </Paragraph>
              <Paragraph display="block">
                If you make a change to your booking, any applicable change fees will need to be paid by you with a credit/charge card. If
                the value of your revised booking is greater than the original booking, you will be required to pay the difference with a
                credit/charge card to secure your revised booking. If the value of your revised booking is less than the original booking,
                you will be refunded the difference to the original method of payment (up to a maximum of the amount paid in cash or Qantas
                Points - any amount paid using a Voucher is non-refundable).
              </Paragraph>
              <Paragraph display="block">
                If you cancel your booking, the value of the Voucher will not be refunded and no replacement Voucher will be issued. Any
                booking cancellation fees cannot be paid for using the Voucher, and will be payable by you with a credit/charge card.
              </Paragraph>

              <Text fontWeight="bold" display="block">
                Credit note vouchers (Credit Vouchers)
              </Text>
              <Paragraph>
                Credit Vouchers are vouchers issued by Qantas Hotels and Qantas Holidays on cancellation of a booking (subject to
                eligibility). Credit Vouchers can be used towards part, or full payment of Qantas Hotels and Qantas Holidays bookings
                through qantas.com (excluding Airbnb). Credit Vouchers must be used within 3 years from the date of issue, or they will
                expire and the value of the Credit Voucher will be forfeited.
              </Paragraph>
              <Paragraph display="block">
                If after using your Credit Voucher there is a balance remaining to be paid, you must pay this balance with a credit/charge
                card or through the use of Qantas Points if you are a member of the Qantas Frequent Flyer program.
              </Paragraph>
              <Paragraph display="block">
                If you make a change to your booking, any applicable fees will need to be paid by you using a credit/charge card. If the
                value of your revised booking is greater than the original booking, you will be required to pay the difference with a
                credit/charge card, Qantas Points, or any remaining balance on your Property Specific Voucher (if applicable). If the value
                of your revised booking is less than the original booking, you will be refunded the difference to the original method of
                payment (where credit/charge card or Qantas Points was used) or provided with a replacement Property Specific Voucher.
              </Paragraph>
            </Box>
          </Text>
        </Modal>
      )}
    </Wrapper>
  );
};

export default VoucherTerms;
